import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { useSyncSlackUsers } from '../../hooks';
import { useSyncEmojis } from '../../hooks/useEmojis';
import {
  Users,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';

interface SyncDetail {
  email: string;
  slackUserId: string;
  displayName: string;
  status: 'updated' | 'not_found' | 'no_change';
}

interface SyncResult {
  synced: number;
  updated: number;
  notFound: number;
  details: SyncDetail[];
}

export const SlackSyncCard: React.FC = () => {
  const { t } = useTranslation('admin');
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [emojiSyncResult, setEmojiSyncResult] = useState<{ synced: number; aliases: number } | null>(null);
  const syncSlackUsers = useSyncSlackUsers();
  const syncEmojis = useSyncEmojis();

  const handleSyncSlackUsers = async () => {
    try {
      setError(null);
      setSyncResult(null);

      const result = await syncSlackUsers.mutateAsync();
      setSyncResult(result);
    } catch (err: any) {
      console.error('Failed to sync Slack users:', err);
      setError(err.response?.data?.message || t('slackSync.error'));
    }
  };

  const handleEmojiSync = async () => {
    try {
      const result = await syncEmojis.mutateAsync();
      setEmojiSyncResult(result);
    } catch (err) {
      console.error('Failed to sync emojis:', err);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'updated':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />{t('slackSync.status.updated')}</Badge>;
      case 'not_found':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />{t('slackSync.status.notFound')}</Badge>;
      case 'no_change':
        return <Badge variant="secondary"><AlertCircle className="h-3 w-3 mr-1" />{t('slackSync.status.noChange')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <Card data-testid="slack-sync-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          <CardTitle>{t('slackSync.title')}</CardTitle>
        </div>
        <CardDescription>
          {t('slackSync.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="border rounded-lg p-4">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-semibold text-lg mb-1">{t('slackSync.syncTitle')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('slackSync.syncDescription')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Button
                onClick={handleSyncSlackUsers}
                disabled={syncSlackUsers.isPending}
                className="gap-2"
                data-testid="slack-sync-button"
              >
                {syncSlackUsers.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('slackSync.syncing')}
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    {t('slackSync.syncNow')}
                  </>
                )}
              </Button>
            </div>

            {/* Error Alert */}
            {error && (
              <Alert variant="destructive" data-testid="slack-sync-error-alert">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Success Summary */}
            {syncResult && (
              <div className="space-y-4">
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-semibold mb-2">{t('slackSync.success')}</div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-muted-foreground">{t('slackSync.totalProcessed')}</div>
                        <div className="text-2xl font-bold">{syncResult.synced}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">{t('slackSync.status.updated')}</div>
                        <div className="text-2xl font-bold text-green-600">{syncResult.updated}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">{t('slackSync.status.notFound')}</div>
                        <div className="text-2xl font-bold text-orange-600">{syncResult.notFound}</div>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>

                {/* Detailed Results */}
                {syncResult.details.length > 0 && (
                  <div className="border rounded-lg">
                    <div className="bg-muted px-4 py-2 font-semibold text-sm">
                      {t('slackSync.details', { count: syncResult.details.length })}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      <table className="w-full">
                        <thead className="bg-muted/50 sticky top-0">
                          <tr className="text-left text-sm">
                            <th className="px-4 py-2 font-medium">{t('slackSync.table.email')}</th>
                            <th className="px-4 py-2 font-medium">{t('slackSync.table.displayName')}</th>
                            <th className="px-4 py-2 font-medium">{t('slackSync.table.slackUserId')}</th>
                            <th className="px-4 py-2 font-medium">{t('slackSync.table.status')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {syncResult.details.map((detail, index) => (
                            <tr key={index} className="text-sm hover:bg-muted/50">
                              <td className="px-4 py-2">{detail.email}</td>
                              <td className="px-4 py-2">{detail.displayName}</td>
                              <td className="px-4 py-2 font-mono text-xs">{detail.slackUserId}</td>
                              <td className="px-4 py-2">{getStatusBadge(detail.status)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Help Text for Not Found Users */}
                {syncResult.notFound > 0 && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <div className="font-semibold mb-1">{t('slackSync.notFoundTitle')}</div>
                      <p className="text-sm">
                        {t('slackSync.notFoundDescription')}
                      </p>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="border rounded-lg p-4 space-y-4" data-testid="slack-emojis-section">
          <div>
            <h3 className="font-semibold text-lg mb-1">{t('slackSync.emojis.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('slackSync.emojis.description')}</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={handleEmojiSync}
              disabled={syncEmojis.isPending}
              variant="outline"
              className="gap-2"
              data-testid="slack-emojis-sync-button"
            >
              {syncEmojis.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  {t('slackSync.emojis.syncing')}
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  {t('slackSync.emojis.sync')}
                </>
              )}
            </Button>
            {emojiSyncResult && (
              <span className="text-sm text-muted-foreground" data-testid="slack-emojis-sync-result">
                {t('slackSync.emojis.result', {
                  synced: emojiSyncResult.synced,
                  aliases: emojiSyncResult.aliases,
                })}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
